import React from 'react';
import { Tooltip as AntTooltip } from 'antd';
import {
  FcdNetworkIcon,
  SimfixAreasIcon,
  PredictIcon,
  TlGreenWaveIcon,
  SignalfixIcon,
  OdAnalysisIcon,
  AssignxIcon,
  MysteryClientIcon,
} from '../../constants/icons';
import { useAuth } from '../../contexts/AuthContext';
import './ModuleSlider.css';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Service key mapping for permissions.
 * Maps internal module names to API permission keys.
 */
const SERVICE_KEYS = {
  fcd_network: 'fcd_network',
  simfix_areas: 'simfix_areas',
  predict: 'predict',
  tl_green_wave: 'tl_green_wave',
  signalfix: 'signalfix',
  od_analysis: 'od_analysis',
  assignx: 'assignx',
  mystery_client: 'mystery_client',
};

/**
 * ModuleSlider Component
 * Sidebar module selector with buttons for each available service.
 * Only renders buttons for services the current user has permission to access.
 *
 * Exclusive-behavior modules (deactivate all others when activated):
 *   - Predict, TL Green Wave, Assignx
 *
 * Page-navigation modules (navigate to their own route):
 *   - OD Analysis (/analysis), Signalfix (/signal-fix), Assignx (/assignx)
 */
function ModuleSlider({
  showNavbarControls,
  setShowNavbarControls,
  showSimfixAreas,
  setShowSimfixAreas,
  showPredictMode,
  setShowPredictMode,
  showTlGreenWave,
  setShowTlGreenWave,
  // Assignx active state is derived from location.pathname; props are accepted
  // by consumers that pass no-ops (e.g. Analysis, Simfix, SignalFix pages).
  // eslint-disable-next-line no-unused-vars
  showAssignx,
  // eslint-disable-next-line no-unused-vars
  setShowAssignx,
  onModuleChange,
  setNetworkViewCategory,
}) {
  const { hasService } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isOnMainPage = location.pathname === '/';
  const isOnAnalysisPage = location.pathname === '/analysis';
  const isOnSignalFixPage = location.pathname === '/signal-fix';
  const isOnAssignxPage = location.pathname === '/assignx';
  const isOnMysteryClientPage = location.pathname === '/mystery-client';

  /**
   * Redirects to the main page and queues a module to be activated.
   */
  const redirectAndActivate = (moduleKey) => {
    sessionStorage.setItem('activateModule', moduleKey);
    navigate('/');
  };

  /** Deactivates all on-map overlay modules (used before activating an exclusive one). */
  const deactivateAllOverlays = () => {
    setShowNavbarControls(false);
    setShowSimfixAreas(false);
    setShowPredictMode(false);
    setShowTlGreenWave(false);
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: Predict (exclusive)                                        */
  /* ------------------------------------------------------------------ */
  const handlePredictClick = () => {
    if (!isOnMainPage) {
      redirectAndActivate('predict');
      return;
    }

    const activating = !showPredictMode;
    if (activating) {
      deactivateAllOverlays();
      setShowPredictMode(true);
      if (onModuleChange) onModuleChange('predict');
    } else {
      setShowPredictMode(false);
      if (onModuleChange) onModuleChange(null);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: TL Green Wave (exclusive)                                  */
  /* ------------------------------------------------------------------ */
  const handleTlGreenWaveClick = () => {
    if (!isOnMainPage) {
      redirectAndActivate('tlgreenwave');
      return;
    }

    const activating = !showTlGreenWave;
    if (activating) {
      deactivateAllOverlays();
      setShowTlGreenWave(true);
      if (onModuleChange) onModuleChange('tlgreenwave');
    } else {
      setShowTlGreenWave(false);
      if (onModuleChange) onModuleChange(null);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: FCD Network (toggleable, deactivates exclusive modules)    */
  /* ------------------------------------------------------------------ */
  const handleFcdClick = () => {
    if (!isOnMainPage) {
      redirectAndActivate('fcd');
      return;
    }

    if (showPredictMode) setShowPredictMode(false);
    if (showTlGreenWave) setShowTlGreenWave(false);

    const activating = !showNavbarControls;
    setShowNavbarControls(activating);
    // On open, default legend/colors to speed (Low–High), matching displayed road colors
    if (activating && setNetworkViewCategory) {
      setNetworkViewCategory('velocita');
    }

    if (onModuleChange && activating) onModuleChange('fcd');
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: SIMFIX Areas (toggleable, deactivates exclusive modules)   */
  /* ------------------------------------------------------------------ */
  const handleSimfixClick = () => {
    if (!isOnMainPage) {
      redirectAndActivate('simfix');
      return;
    }

    if (showPredictMode) setShowPredictMode(false);
    if (showTlGreenWave) setShowTlGreenWave(false);

    setShowSimfixAreas(!showSimfixAreas);

    if (onModuleChange && !showSimfixAreas) onModuleChange('simfix');
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: OD Analysis (navigates to /analysis)                      */
  /* ------------------------------------------------------------------ */
  const handleODAnalysisClick = () => {
    if (!isOnAnalysisPage) navigate('/analysis');
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: Signalfix (navigates to /signal-fix)                      */
  /* ------------------------------------------------------------------ */
  const handleSignalfixClick = () => {
    if (!isOnSignalFixPage) navigate('/signal-fix');
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: Assignx (exclusive + navigates to /assignx)               */
  /*  - Activating: deactivate all overlay modules, navigate to /assignx */
  /*  - Deactivating (already on /assignx): navigate back to main map    */
  /* ------------------------------------------------------------------ */
  const handleAssignxClick = () => {
    if (isOnAssignxPage) {
      navigate('/');
    } else {
      deactivateAllOverlays();
      if (onModuleChange) onModuleChange(null);
      navigate('/assignx');
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: Mystery Client (navigates to /mystery-client)             */
  /*  The dashboard opens on its own overview of every city, so one      */
  /*  click goes straight there instead of previewing a single zone.     */
  /* ------------------------------------------------------------------ */
  const handleMysteryClientClick = () => {
    if (isOnMysteryClientPage) return; // Already on the page
    navigate('/mystery-client');
  };

  /* ------------------------------------------------------------------ */
  /*  Render                                                              */
  /* ------------------------------------------------------------------ */
  return (
    <div className="module-slider">
      {hasService(SERVICE_KEYS.fcd_network) && (
        <AntTooltip title="FCD NETX" placement="left">
          <button
            className={`module-slider-btn ${showNavbarControls ? 'active' : ''}`}
            onClick={handleFcdClick}
            aria-label="FCD NETX"
          >
            <img src={FcdNetworkIcon} alt="FCD NETX" className="module-icon" />
          </button>
        </AntTooltip>
      )}

      {hasService(SERVICE_KEYS.simfix_areas) && (
        <AntTooltip title="SIMFIX" placement="left">
          <button
            className={`module-slider-btn ${showSimfixAreas ? 'active' : ''}`}
            onClick={handleSimfixClick}
            aria-label="SIMFIX"
          >
            <img src={SimfixAreasIcon} alt="SIMFIX" className="module-icon" />
          </button>
        </AntTooltip>
      )}

      {hasService(SERVICE_KEYS.predict) && (
        <AntTooltip title="PREDICTX" placement="left">
          <button
            className={`module-slider-btn ${showPredictMode ? 'active' : ''}`}
            onClick={handlePredictClick}
            aria-label="PREDICTX"
          >
            <img src={PredictIcon} alt="PREDICTX" className="module-icon" />
          </button>
        </AntTooltip>
      )}

      {hasService(SERVICE_KEYS.tl_green_wave) && (
        <AntTooltip title="SYNCFIX" placement="left">
          <button
            className={`module-slider-btn ${showTlGreenWave ? 'active' : ''}`}
            onClick={handleTlGreenWaveClick}
            aria-label="SYNCFIX"
          >
            <img src={TlGreenWaveIcon}alt="SYNCFIX" className="module-icon" />
          </button>
        </AntTooltip>
      )}

      {hasService(SERVICE_KEYS.signalfix) && (
        <AntTooltip title="SIGNALFIX" placement="left">
          <button
            className={`module-slider-btn ${isOnSignalFixPage ? 'active' : ''}`}
            onClick={handleSignalfixClick}
            aria-label="SIGNALFIX"
          >
            <img src={SignalfixIcon} alt="SIGNALFIX" className="module-icon" />
          </button>
        </AntTooltip>
      )}

      {hasService(SERVICE_KEYS.od_analysis) && (
        <AntTooltip title="ODX" placement="left">
          <button
            className={`module-slider-btn ${isOnAnalysisPage ? 'active' : ''}`}
            onClick={handleODAnalysisClick}
            aria-label="ODX"
          >
            <img src={OdAnalysisIcon} alt="ODX" className="module-icon" />
          </button>
        </AntTooltip>
      )}

      {hasService(SERVICE_KEYS.assignx) && (
        <AntTooltip title="ASSIGNX" placement="left">
          <button
            className={`module-slider-btn ${isOnAssignxPage ? 'active' : ''}`}
            onClick={handleAssignxClick}
            aria-label="ASSIGNX"
          >
            <img src={AssignxIcon} alt="ASSIGNX" className="module-icon" />
          </button>
        </AntTooltip>
      )}

      {hasService(SERVICE_KEYS.mystery_client) && (
        <AntTooltip title="MYSTERY CLIENT" placement="left">
          <button
            className={`module-slider-btn ${isOnMysteryClientPage ? 'active' : ''}`}
            onClick={handleMysteryClientClick}
            aria-label="MYSTERY CLIENT"
          >
            <img src={MysteryClientIcon} alt="MYSTERY CLIENT" className="module-icon" />
          </button>
        </AntTooltip>
      )}
    </div>
  );
}

export default ModuleSlider;
